import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Organization } from '../../../core/models/organization.model';
import { User } from '../models/user.model';
import { Modal } from '../../../shared/components/modal/modal';
import { ModalFooterDirective } from '../../../shared/components/modal/modal-footer.directive';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { Toggle } from '../../../shared/components/forms/toggle';

export type DialogMode = 'add' | 'view';

@Component({
  selector: 'app-create-user-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, Modal, ModalFooterDirective, FormField, TextInput, Toggle],
  templateUrl: './create-user-dialog.component.html',
  styleUrl: './create-user-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateUserDialogComponent implements OnChanges {
  private readonly fb = inject(FormBuilder);

  readonly open = signal(true);

  @Input() organizations: Organization[] = [];
  @Input() mode: DialogMode = 'add';
  @Input() user: User | null = null;
  @Input() selectedTenantIds: string[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<{
    email: string;
    firstName: string;
    lastName: string;
    organizationIds: string[];
  }>();

  // No username control — the realm uses EmailAsUsername.
  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    organizationControls: this.fb.array([], this.minSelectedCheckboxes(1)),
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organizations'] && this.organizations) {
      this.createTenantCheckboxes();
    }
    if (this.user) {
      this.form.patchValue(this.user);
      if (this.mode === 'view') {
        this.form.disable();
      } else {
        this.form.enable();
      }
    } else {
      this.form.reset();
    }

    if (changes['selectedTenantIds'] && this.selectedTenantIds && this.organizations.length > 0) {
      this.selectedTenantIds.forEach(selectedId => {
        const index = this.organizations.findIndex(organization => organization.id === selectedId);
        if (index !== -1) {
          this.organizationControls.at(index).setValue(true);
        }
      });
    }
  }

  get organizationControls(): FormArray {
    return this.form.get('organizationControls') as FormArray;
  }

  onClose(): void {
    this.close.emit();
  }

  onSubmit(): void {
    if (this.form.valid) {
      const selectedTenantIds = this.form.value.organizationControls
        ?.map((checked, i) => (checked ? this.organizations[i].id : null))
        .filter((v): v is string => v !== null) ?? [];

      this.save.emit({
        email: this.form.value.email!,
        firstName: this.form.value.firstName!,
        lastName: this.form.value.lastName!,
        organizationIds: selectedTenantIds,
      });
    }
  }

  private createTenantCheckboxes() {
    this.organizationControls.clear();
    this.organizations.forEach(() => {
      this.organizationControls.push(new FormControl(false));
    });
    // Mark as touched to display validation message immediately if invalid
    this.organizationControls.markAsTouched();
  }

  private minSelectedCheckboxes(min = 1): ValidatorFn {
    return (control: AbstractControl) => {
      if (control instanceof FormArray) {
        const totalSelected = control.controls
          .map(c => c.value)
          .reduce((prev, next) => (next ? prev + 1 : prev), 0);
        return totalSelected >= min ? null : { required: true };
      }
      return null;
    };
  }
}
